// components/Sidebar.tsx
import React, { DragEvent } from 'react';
import styled from 'styled-components';
import ElementCard from './ElementCard';
import SearchBar from '../searchBar/SearchBar';
import { hexToRgba } from '../../utils/helper';
import IdeaIcon from '../../assets/icons/IdeaIcon';
import StyledText from '../StyledComponents/StyledText';
import { useTheme } from '../../middleware/ThemeContext';
import { SkeletonCell } from '../SkeletonCell';
import { Theme } from '../../utils/themes';

const SidebarContainer = styled.div<{ theme: Theme }>`
  width: 378px;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-right: 1px solid #e9ecef;
  padding: 20px 10px;
  overflow-y: auto;
  height: calc(100vh - 149px);
`;

const SearchBox = styled.div`
  position: relative;
  margin-bottom: 20px;
  
  input {
    width: 100%;
    padding: 8px 12px 8px 32px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-size: 13px;
    outline: none;
    
    &:focus {
      border-color: #6c63ff;
    }
  }
  
  &::before {
    content: '🔍';
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 14px;
  }
`;

const InfoBox = styled.div<{ theme: Theme }>`
display: flex;
 background-color: ${({theme}) => hexToRgba(theme.CorporateTheme.colors.melonYellow, 0.10)};
  border: 1px solid ${({theme}) => hexToRgba(theme.CorporateTheme.colors.melonYellow, 1)}; 
  border-radius: 5px;
    backdrop-filter: blur(20px);
    padding:10px;
    gap:8px;
    margin-top:20px;
`;

const SectionTitle = styled.h6`
  font-size: 11px;
  text-transform: uppercase;
  color: #6c757d;
  font-weight: 600;
  margin: 20px 0 12px 0;
  letter-spacing: 0.5px;
`;

const ElementsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 20px;
`;

const EmptyElementsState = styled.div<{ theme: Theme }>`
  grid-column: 1 / -1;
  min-height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.border, 0.06)};
  padding: 12px;
  text-align: center;
`;

interface SidebarProps {
  basicElements: Array<{ type: string; label: string; icon: React.ReactNode }>;
  sections: Array<{ type: string; label: string; icon: React.ReactNode }>;
  draggedElement: string | null;
  setDraggedElement: (element: string | null) => void;
  isLoading?: boolean;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  basicElements,
  sections,
  draggedElement,
  setDraggedElement,
  isLoading = false,
  searchTerm = '',
  onSearchChange
}) => {
  const { theme } = useTheme();
  const hasActiveSearch = searchTerm.trim().length > 0;

  const handleDragEnd = () => {
    setDraggedElement(null);
  };

  if (isLoading && !hasActiveSearch) {
    return (
      <SidebarContainer theme={theme}>
        <SkeletonCell height="36px" width="100%" />
        <InfoBox theme={theme}>
          <div><IdeaIcon size={24} /></div>
          <StyledText style={{ marginTop: 5 }} lineHeight='1.5' fontSize='14px' fontStyle='italic' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
            Loading page elements...
          </StyledText>
        </InfoBox>
        <StyledText className='mb-3 mt-4' fontSize='12px' fontWeight={600} textColor={theme.CorporateTheme.colors.textSecondary}>
          Basic Elements
        </StyledText>
        <ElementsGrid>
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCell key={`basic-skel-${index}`} height="137px" width="100%" />
          ))}
        </ElementsGrid>
        <StyledText className='mb-3 mt-4' fontSize='12px' fontWeight={600} textColor={theme.CorporateTheme.colors.textSecondary}>
          Sections
        </StyledText>
        <ElementsGrid>
          {Array.from({ length: 2 }).map((_, index) => (
            <SkeletonCell key={`section-skel-${index}`} height="137px" width="100%" />
          ))}
        </ElementsGrid>
      </SidebarContainer>
    );
  }

  return (
    <SidebarContainer theme={theme}>
      <SearchBar
        placeholder="Search or type a command"
        style={{ width: '100%' }}
        onSearchChange={onSearchChange}
        searchTerm={searchTerm}
        showLoader={isLoading && hasActiveSearch}
        
      />

      <InfoBox theme={theme}>
        <div><IdeaIcon size={24} /></div>
        {/* <Sty>
          Drag any element and drop in the canvas to edit its properties in the right panel.
        </span> */}
        <StyledText style={{ marginTop: 5 }} lineHeight='1.5' fontSize='14px' fontStyle='italic' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
          Drag any element and drop in the canvas to edit its properties in the right panel.
        </StyledText>
      </InfoBox>

      <StyledText className='mb-3 mt-4' fontSize='12px' fontWeight={600} textColor={theme.CorporateTheme.colors.textSecondary}>Basic Elements</StyledText>

      <ElementsGrid>
        {basicElements.length > 0 ? (
          basicElements.map((element) => (
            <ElementCard
              key={element.type}
              element={element}
              draggedElement={draggedElement}
              setDraggedElement={setDraggedElement}
              onDragEnd={handleDragEnd}
            />
          ))
        ) : (
          <EmptyElementsState theme={theme}>
            <StyledText fontSize='13px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>
              No basic elements found.
            </StyledText>
          </EmptyElementsState>
        )}
      </ElementsGrid>

      <StyledText className='mb-3 mt-4' fontSize='12px' fontWeight={600} textColor={theme.CorporateTheme.colors.textSecondary}>Sections</StyledText>
      <ElementsGrid>
        {sections.map((element) => (
          <ElementCard
            key={element.type}
            element={element}
            draggedElement={draggedElement}
            setDraggedElement={setDraggedElement}
            onDragEnd={handleDragEnd}
          />
        ))}
      </ElementsGrid>
    </SidebarContainer>
  );
};

export default Sidebar;